import { useState, type FormEvent } from 'react';
import type { UserSettings } from '@firegame/shared';
import { changePassword } from '../api/auth.js';
import { ApiClientError } from '../api/client.js';
import { useAuth } from '../store/AuthContext.js';
import { Button } from '../ui/Button.js';
import { Field } from '../ui/Field.js';
import { t } from '../i18n/index.js';
import './ProfilePage.css';

/** Curseur de volume, avec valeur affichee et accessible au clavier. */
function VolumeSlider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="fg-profile__slider">
      <span className="fg-profile__slider-label">{label}</span>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <output className="fg-profile__slider-value">{value}</output>
    </label>
  );
}

export function ProfilePage() {
  const labels = t();
  const auth = useAuth();

  const [draft, setDraft] = useState<UserSettings | null>(auth.settings);
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [savingPassword, setSavingPassword] = useState(false);

  // Les preferences arrivent avec la session, apres le premier rendu. On ajuste
  // le brouillon pendant le rendu plutot que dans un effet : c'est le motif
  // recommande par React pour deriver un etat d'une valeur externe, et cela
  // evite un rendu supplementaire.
  const [syncedSettings, setSyncedSettings] = useState(auth.settings);
  if (auth.settings !== syncedSettings) {
    setSyncedSettings(auth.settings);
    setDraft(auth.settings);
  }

  if (!auth.user || !draft) return null;

  function update<Key extends keyof UserSettings>(key: Key, value: UserSettings[Key]): void {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  }

  async function saveSettings(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!draft) return;

    setSettingsError(null);
    setSettingsMessage(null);
    setSavingSettings(true);

    try {
      await auth.updateSettings(draft);
      setSettingsMessage(labels.auth.profile.settingsSaved);
    } catch (cause) {
      setSettingsError(cause instanceof ApiClientError ? cause.message : labels.errors.unexpected);
    } finally {
      setSavingSettings(false);
    }
  }

  async function submitPassword(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPasswordError(null);
    setPasswordMessage(null);
    setSavingPassword(true);

    try {
      await changePassword({ currentPassword, newPassword });
      setPasswordMessage(labels.auth.profile.passwordChanged);
      setCurrentPassword('');
      setNewPassword('');
    } catch (cause) {
      setPasswordError(cause instanceof ApiClientError ? cause.message : labels.errors.unexpected);
    } finally {
      setSavingPassword(false);
    }
  }

  const createdAt = new Date(auth.user.createdAt).toLocaleDateString('fr-FR');
  const lastLoginAt = auth.user.lastLoginAt
    ? new Date(auth.user.lastLoginAt).toLocaleString('fr-FR')
    : labels.auth.profile.never;

  return (
    <div className="fg-profile">
      <h1>{labels.auth.profile.title}</h1>

      <section className="fg-profile__section">
        <h2>{labels.auth.profile.account}</h2>
        <dl className="fg-profile__grid">
          <div>
            <dt>{labels.auth.displayNameLabel}</dt>
            <dd>{auth.user.displayName}</dd>
          </div>
          <div>
            <dt>{labels.auth.emailLabel}</dt>
            <dd>{auth.user.email}</dd>
          </div>
          <div>
            <dt>{labels.auth.profile.role}</dt>
            <dd>
              {auth.user.role === 'ADMIN'
                ? labels.auth.profile.roleAdmin
                : labels.auth.profile.rolePlayer}
            </dd>
          </div>
          <div>
            <dt>{labels.auth.profile.memberSince}</dt>
            <dd>{createdAt}</dd>
          </div>
          <div>
            <dt>{labels.auth.profile.lastLogin}</dt>
            <dd>{lastLoginAt}</dd>
          </div>
        </dl>
      </section>

      <section className="fg-profile__section">
        <h2>{labels.auth.profile.settings}</h2>
        <form onSubmit={saveSettings}>
          {settingsError && (
            <div className="fg-alert fg-alert--error" role="alert">
              {settingsError}
            </div>
          )}
          {settingsMessage && (
            <div className="fg-alert fg-alert--success" role="status">
              {settingsMessage}
            </div>
          )}

          <VolumeSlider
            label={labels.auth.profile.volumeMaster}
            value={draft.volumeMaster}
            onChange={(value) => update('volumeMaster', value)}
          />
          <VolumeSlider
            label={labels.auth.profile.volumeMusic}
            value={draft.volumeMusic}
            onChange={(value) => update('volumeMusic', value)}
          />
          <VolumeSlider
            label={labels.auth.profile.volumeSfx}
            value={draft.volumeSfx}
            onChange={(value) => update('volumeSfx', value)}
          />
          <VolumeSlider
            label={labels.auth.profile.volumeAmbience}
            value={draft.volumeAmbience}
            onChange={(value) => update('volumeAmbience', value)}
          />

          <label className="fg-profile__check">
            <input
              type="checkbox"
              checked={draft.reducedEffects}
              onChange={(event) => update('reducedEffects', event.target.checked)}
            />
            <span>
              {labels.auth.profile.reducedEffects}
              <small>{labels.auth.profile.reducedEffectsHint}</small>
            </span>
          </label>

          <label className="fg-profile__check">
            <input
              type="checkbox"
              checked={draft.highContrast}
              onChange={(event) => update('highContrast', event.target.checked)}
            />
            <span>{labels.auth.profile.highContrast}</span>
          </label>

          <label className="fg-profile__check">
            <input
              type="checkbox"
              checked={draft.showHints}
              onChange={(event) => update('showHints', event.target.checked)}
            />
            <span>{labels.auth.profile.showHints}</span>
          </label>

          <Button type="submit" busy={savingSettings}>
            {labels.auth.profile.saveSettings}
          </Button>
        </form>
      </section>

      <section className="fg-profile__section">
        <h2>{labels.auth.profile.changePassword}</h2>
        <form onSubmit={submitPassword} noValidate>
          {passwordError && (
            <div className="fg-alert fg-alert--error" role="alert">
              {passwordError}
            </div>
          )}
          {passwordMessage && (
            <div className="fg-alert fg-alert--success" role="status">
              {passwordMessage}
            </div>
          )}

          <Field
            label={labels.auth.currentPasswordLabel}
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            disabled={savingPassword}
          />

          <Field
            label={labels.auth.newPasswordLabel}
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            hint={labels.auth.passwordHint}
            disabled={savingPassword}
          />

          <Button type="submit" variant="secondary" busy={savingPassword}>
            {labels.auth.profile.changePassword}
          </Button>
        </form>
      </section>
    </div>
  );
}
